import { Alert } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useFormState } from 'react-hook-form';
import { handleUnauthorizedResponse } from '../../api/http';
import PolicyApi from '../../api/PolicyApi';
import { AuthData } from '../../context/auth';
import { ErrorData } from '../../context/ErrorProvider';
import { PolicyData, withPolicyContext } from '../../context/policy';
import { PRICE_PREVIEW_UNSAVED_CHANGES_MSG } from '../../data/labelsAndMessages';
import {
	AutomatedUnderwritingResult,
	PolicyPriceResponse,
} from '../../types/policy';
import { isEmpty, policyIsWithdrawn } from '../../util/util';
import {
	StyledContainer,
	StyledSectionContainer,
	StyledTitle,
} from '../common';
import LargeCenterSpinner from '../LargeCenterSpinner';
import PricePreviewTables from './QuotePricePreviewModal/PricePreviewTables';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	errorData: ErrorData;
}

const QuotePricePreview = (props: Props) => {
	const [priceData, setPriceData] = useState<PolicyPriceResponse>(null);
	const [underwritingData, setUnderwriting] =
		useState<AutomatedUnderwritingResult>(null);
	const { isDirty } = useFormState();

	const { authData, policyData, errorData } = props;

	const {
		policy,
		productConfig,
		quoteUpdateObj,
		policyUpdateObj,
		selectedQuote,
	} = policyData;

	const hasUnsavedChanges =
		isDirty || !isEmpty(quoteUpdateObj) || !isEmpty(policyUpdateObj);

	const authToken = authData.auth.authorizationToken;
	const { locator: policyLocator } = policy;
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const source = axios.CancelToken.source();

		const load = async () => {
			setLoading(true);
			try {
				let priceResponse = null;
				let underWritingResult = null;
				if (selectedQuote.state === 'draft') {
					// if quote is draft, we need to call rating and underwriting to get pricing/underwriting data
					priceResponse = await PolicyApi.priceQuote(authToken, policyLocator, {
						cancelToken: source.token,
					});
					const underWritingResponse =
						await PolicyApi.getAutomatedUnderwritingResult(
							authToken,
							policyLocator,
							{ cancelToken: source.token },
						);
					underWritingResult = underWritingResponse.data;
				} else {
					// if quote is locked, we can fetch existing pricing and use the underwriting data on the quote
					priceResponse = await PolicyApi.fetchQuotePricing(
						authToken,
						selectedQuote.locator,
						{ cancelToken: source.token },
					);
					underWritingResult = selectedQuote.automatedUnderwritingResult;
				}

				if (priceResponse && underWritingResult) {
					setPriceData(priceResponse.data);
					setUnderwriting(underWritingResult);
				}
			} catch (error) {
				if (axios.isCancel(error)) {
					return;
				} else {
					errorData.handleErrorResponse(
						error,
						'Failed to fetch necessary pricing data',
					);
					if (await handleUnauthorizedResponse(error, authData)) {
						return;
					}
				}
			}

			setLoading(false);
		};

		if (!policyIsWithdrawn(policy)) {
			load();
		} else {
			setLoading(false);
		}

		return () => {
			source.cancel();
		};
	}, [quoteUpdateObj, policyUpdateObj]);

	const renderPriceTables = () => {
		if (!priceData || !underwritingData) {
			return renderNullPricing();
		}

		return (
			<PricePreviewTables
				productConfig={productConfig}
				priceData={priceData}
				underwritingData={underwritingData}
			/>
		);
	};

	const renderNoPricing = () => {
		return (
			<div style={{ textAlign: 'center' }}>
				No pricing available for withdrawn policy
			</div>
		);
	};

	const renderNullPricing = () => {
		return <div style={{ textAlign: 'center' }}>No pricing available</div>;
	};

	const renderUnsavedChangesWarning = () => {
		return (
			<Alert severity='warning'>{PRICE_PREVIEW_UNSAVED_CHANGES_MSG()}</Alert>
		);
	};

	if (loading) {
		return <LargeCenterSpinner />;
	}

	return (
		<>
			<StyledContainer>
				<StyledTitle>Pricing Breakdown</StyledTitle>
				{hasUnsavedChanges && !policyIsWithdrawn(policy)
					? renderUnsavedChangesWarning()
					: null}
			</StyledContainer>
			<hr />
			<StyledSectionContainer>
				{policyIsWithdrawn(policy) ? renderNoPricing() : renderPriceTables()}
			</StyledSectionContainer>
		</>
	);
};

export default withPolicyContext<Props>(QuotePricePreview);
